// client/src/pages/admin/AdminBusinessDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeft, Building2, Mail, Phone, MapPin, Calendar,
  ShoppingCart, Ban, CheckCircle, Edit, Save, X, FileText, Hash
} from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { adminAPI } from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';

const SERVICE_AREAS = [
  { group: 'Crete', areas: ['heraklion', 'chania', 'rethymno', 'lasithi'] },
  { group: 'Athens', areas: ['athens_central', 'athens_north', 'athens_south', 'athens_east', 'athens_west', 'piraeus', 'east_attica', 'west_attica'] },
  { group: 'Other', areas: ['thessaloniki', 'central_macedonia', 'larissa', 'volos', 'patras', 'rhodes', 'corfu', 'mykonos', 'santorini'] }
];
const BUSINESS_TYPES = ['restaurant', 'cafe', 'hotel', 'catering', 'retail', 'food_service', 'bakery', 'bar', 'other'];

// ================================================================
// EDIT FORM — Isolated component, NO inline component defs
// All inputs are raw <input> elements so React never remounts them
// ================================================================
const BusinessEditForm = ({ initialData, onSave, onCancel }) => {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(initialData)));
  const [saving, setSaving] = useState(false);

  // Deep get helper
  const get = (path) => path.split('.').reduce((o, k) => o?.[k], form) ?? '';

  // Deep set helper — immutable update
  const set = useCallback((path, value) => {
    setForm(prev => {
      const clone = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let ref = clone;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!ref[keys[i]]) ref[keys[i]] = {};
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return clone;
    });
  }, []);

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  const inputCls = "w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none";
  const labelCls = "block text-xs font-medium text-gray-500 mb-1";

  return (
    <div className="space-y-4">
      {/* Business Info */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Business Information</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>Business Name</label>
            <input type="text" value={get('businessName')} onChange={e => set('businessName', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Legal Name</label>
            <input type="text" value={get('legalName')} onChange={e => set('legalName', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Business Type</label>
            <select value={get('businessType')} onChange={e => set('businessType', e.target.value)} className={inputCls}>
              {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select></div>
          <div><label className={labelCls}>Account Status</label>
            <select value={get('accountStatus') || 'pending'} onChange={e => set('accountStatus', e.target.value)} className={inputCls}>
              <option value="pending">Pending</option><option value="approved">Approved</option>
              <option value="rejected">Rejected</option><option value="suspended">Suspended</option>
            </select></div>
        </div>
      </div>

      {/* Fiscal */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Fiscal Information</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>VAT Number</label>
            <input type="text" value={get('vatNumber')} onChange={e => set('vatNumber', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Tax Office</label>
            <input type="text" value={get('taxOffice')} onChange={e => set('taxOffice', e.target.value)} className={inputCls} /></div>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Contact</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>Contact Person</label>
            <input type="text" value={get('contact.contactPerson')} onChange={e => set('contact.contactPerson', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Phone</label>
            <input type="text" value={get('contact.phone')} onChange={e => set('contact.phone', e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Email</label>
            <input type="text" value={get('contact.email')} onChange={e => set('contact.email', e.target.value)} className={inputCls} /></div>
        </div>
      </div>

      {/* Address */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Address</h2>
        <div className="space-y-3">
          <div><label className={labelCls}>Street</label>
            <input type="text" value={get('address.street')} onChange={e => set('address.street', e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>City</label>
              <input type="text" value={get('address.city')} onChange={e => set('address.city', e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Postal Code</label>
              <input type="text" value={get('address.zipCode')} onChange={e => set('address.zipCode', e.target.value)} className={inputCls} /></div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Service Areas</h2>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {SERVICE_AREAS.map(group => (
            <div key={group.group}>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-1">{group.group}</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {group.areas.map(area => {
                  const selected = (form.serviceAreas || []).includes(area);
                  return (
                    <button key={area} type="button" onClick={() => {
                      const cur = form.serviceAreas || [];
                      set('serviceAreas', selected ? cur.filter(a => a !== area) : [...cur, area]);
                    }} className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      selected ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                    }`}>{area.replace(/_/g, ' ')}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button onClick={handleSave} disabled={saving}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2 text-sm">
          <X className="w-4 h-4" /> Cancel
        </button>
      </div>
    </div>
  );
};

// ================================================================
// VIEW HELPER (stable — defined outside any component)
// ================================================================
const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 break-words">{value || 'N/A'}</p>
    </div>
  </div>
);

// ================================================================
// MAIN PAGE
// ================================================================
const AdminBusinessDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => { fetchBusinessDetail(); }, [id]);

  const fetchBusinessDetail = async () => {
    try { setLoading(true); const r = await adminAPI.getBusinessDetail(id); setData(r.data.data || r.data); }
    catch { setError('Failed to load business details'); }
    finally { setLoading(false); }
  };

  const handleSave = async (formData) => {
    try { await adminAPI.updateBusiness(id, formData); setEditing(false); fetchBusinessDetail(); }
    catch (err) { toast.error('Failed: ' + (err.response?.data?.message || err.message)); }
  };

  const handleBlockUnblock = async () => {
    const action = data.business.isActive ? 'Block' : 'Unblock';
    setConfirmModal({
      title: `${action} Business`,
      message: `${action} this business?`,
      icon: 'warning',
      confirmLabel: action,
      confirmColor: 'yellow',
      onConfirm: async () => {
        setConfirmModal(null);
        try { await adminAPI.blockUnblockBusiness(id, { isActive: !data.business.isActive }); fetchBusinessDetail(); }
        catch { toast.error('Failed to update status'); }
      }
    });
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="flex justify-center items-center h-64 text-gray-500">Loading...</div><Footer /></div>;
  if (error || !data) return <div className="min-h-screen bg-gray-50"><Navbar /><div className="container mx-auto px-4 py-8 pt-16 lg:pt-8"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error || 'Not found'}</div></div><Footer /></div>;

  const { business, stats, recentOrders } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8 pt-16 lg:pt-8 max-w-6xl">
        <div className="mb-6">
          <button onClick={() => navigate('/admin/users/businesses')} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Business Users
          </button>
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{business.businessName}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${business.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{business.isActive ? 'Active' : 'Blocked'}</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">{business.accountStatus || 'pending'}</span>
                <span className="text-xs text-gray-400">Registered: {business.createdAt ? format(new Date(business.createdAt), 'MMM dd, yyyy') : 'N/A'}</span>
              </div>
            </div>
            {!editing && (
              <div className="flex gap-2">
                <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"><Edit className="w-4 h-4" /> Edit</button>
                <button onClick={handleBlockUnblock} className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm ${business.isActive ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                  {business.isActive ? <><Ban className="w-4 h-4" /> Block</> : <><CheckCircle className="w-4 h-4" /> Unblock</>}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Total Orders</p><p className="text-2xl font-bold text-gray-900">{stats?.totalOrders || 0}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Total Spent</p><p className="text-2xl font-bold text-green-600">€{(stats?.totalSpent || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Avg Order</p><p className="text-2xl font-bold text-blue-600">€{(stats?.averageOrder || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-lg shadow-sm border p-4"><p className="text-xs text-gray-500 mb-1">Suppliers</p><p className="text-2xl font-bold text-purple-600">{stats?.uniqueWholesalers || 0}</p></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            {editing ? (
              <BusinessEditForm key={business._id} initialData={business} onSave={handleSave} onCancel={() => setEditing(false)} />
            ) : (
              <div className="space-y-4">
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Business Information</h2>
                  <InfoRow icon={Building2} label="Business Type" value={business.businessType?.replace('_', ' ')} />
                  <InfoRow icon={FileText} label="Legal Name" value={business.legalName} />
                  <InfoRow icon={Calendar} label="Registered" value={business.createdAt ? format(new Date(business.createdAt), 'MMMM dd, yyyy') : 'N/A'} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Fiscal</h2>
                  <InfoRow icon={Hash} label="VAT Number" value={business.vatNumber} />
                  <InfoRow icon={FileText} label="Tax Office" value={business.taxOffice} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Contact</h2>
                  <InfoRow icon={Building2} label="Contact Person" value={business.contact?.contactPerson} />
                  <InfoRow icon={Phone} label="Phone" value={business.contact?.phone} />
                  <InfoRow icon={Mail} label="Email" value={business.contact?.email || business.email} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Address</h2>
                  <InfoRow icon={MapPin} label="Street" value={business.address?.street} />
                  <InfoRow icon={MapPin} label="City / Postal" value={`${business.address?.city || ''} ${business.address?.zipCode || ''}`} /></div>
                <div className="bg-white rounded-lg shadow-sm border p-5"><h2 className="text-sm font-semibold text-gray-900 mb-3">Service Areas</h2>
                  <div className="flex flex-wrap gap-1.5">
                    {(business.serviceAreas || []).length > 0 ? business.serviceAreas.map(a => (
                      <span key={a} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">{a.replace(/_/g, ' ')}</span>
                    )) : <span className="text-sm text-gray-400">No areas set</span>}
                  </div></div>
              </div>
            )}
          </div>
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border p-5">
              <div className="flex justify-between items-center mb-4"><h2 className="text-sm font-semibold text-gray-900">Recent Orders</h2><span className="text-xs text-gray-400">{stats?.totalOrders || 0} total</span></div>
              {(!recentOrders || recentOrders.length === 0) ? (
                <div className="text-center py-12"><ShoppingCart className="w-12 h-12 text-gray-200 mx-auto mb-3" /><p className="text-gray-400 text-sm">No orders yet</p></div>
              ) : (
                <div className="space-y-2">{recentOrders.slice(0, 10).map(order => (
                  <div key={order._id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                    <div><div className="flex items-center gap-2 mb-0.5"><span className="text-sm font-medium text-gray-900">{order.orderNumber}</span>
                      <span className={`px-2 py-0.5 text-xs rounded capitalize ${order.status === 'delivered' ? 'bg-green-100 text-green-800' : order.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{order.status}</span></div>
                      <p className="text-xs text-gray-500">{order.wholesalerId?.companyName || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{order.createdAt ? format(new Date(order.createdAt), 'MMM dd, yyyy h:mm a') : ''}</p></div>
                    <div className="text-right"><p className="text-sm font-bold text-gray-900">€{(order.totals?.total || 0).toFixed(2)}</p><p className="text-xs text-gray-400">{order.items?.length || 0} items</p></div>
                  </div>))}</div>)}
            </div>
          </div>
        </div>
      </main>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminBusinessDetail;